import { FormField, CheckboxGroupField, FieldWidth } from '@/types/formSchema'

interface FieldLayoutEditorProps {
  field: Partial<FormField | CheckboxGroupField>
  onChange: (updates: Partial<FormField | CheckboxGroupField>) => void
}

export default function FieldLayoutEditor({ field, onChange }: FieldLayoutEditorProps) {
  const widthOptions: { value: FieldWidth; label: string; description: string }[] = [
    { value: 'full', label: 'Full Width (100%)', description: 'Chiếm toàn bộ chiều rộng' },
    { value: 'half', label: 'Half Width (50%)', description: 'Chiếm 1/2 chiều rộng' },
    { value: 'third', label: 'Third Width (33%)', description: 'Chiếm 1/3 chiều rộng' },
    { value: 'quarter', label: 'Quarter Width (25%)', description: 'Chiếm 1/4 chiều rộng' }
  ]

  return (
    <div className="border-t pt-4 mt-4">
      <h4 className="font-semibold text-sm mb-3 text-gray-700">📐 Layout Options</h4>

      {/* Width Selection */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Độ rộng field:
        </label>
        <div className="grid grid-cols-2 gap-2">
          {widthOptions.map((option) => (
            <label
              key={option.value}
              className={`
                flex items-center p-3 border-2 rounded-lg cursor-pointer transition-all
                ${field.width === option.value || (!field.width && option.value === 'full')
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
                }
              `}
            >
              <input
                type="radio"
                name="field-width"
                value={option.value}
                checked={field.width === option.value || (!field.width && option.value === 'full')}
                onChange={() => onChange({ width: option.value })}
                className="mr-2"
              />
              <div className="flex-1">
                <div className="text-sm font-medium">{option.label}</div>
                <div className="text-xs text-gray-500">{option.description}</div>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* New Row Option */}
      <div className="mb-4">
        <label className="flex items-center space-x-2 cursor-pointer p-3 border rounded-lg hover:bg-gray-50">
          <input
            type="checkbox"
            checked={field.newRow || false}
            onChange={(e) => onChange({ newRow: e.target.checked })}
            className="w-4 h-4 text-blue-600 rounded"
          />
          <div className="flex-1">
            <span className="text-sm font-medium">Bắt đầu dòng mới</span>
            <p className="text-xs text-gray-500 mt-1">
              Field này sẽ được hiển thị ở đầu dòng mới (không ghép với field trước)
            </p>
          </div>
        </label>
      </div>

      {/* Visual Preview */}
      <div className="bg-gray-50 p-3 rounded-lg">
        <div className="text-xs font-medium text-gray-600 mb-2">Preview:</div>
        <div className="bg-white p-2 rounded border border-gray-200">
          <div className="flex flex-wrap gap-2">
            {field.newRow && (
              <div className="w-full text-xs text-orange-600">↓ Dòng mới</div>
            )}
            <div
              className={`
                bg-blue-100 border border-blue-300 rounded px-2 py-1 text-xs
                ${field.width === 'full' || !field.width ? 'w-full' : ''}
                ${field.width === 'half' ? 'w-[calc(50%-0.25rem)]' : ''}
                ${field.width === 'third' ? 'w-[calc(33.333%-0.333rem)]' : ''}
                ${field.width === 'quarter' ? 'w-[calc(25%-0.375rem)]' : ''}
              `}
            >
              {field.label || 'Field Preview'}
            </div>
            {field.width !== 'full' && (
              <>
                <div
                  className={`
                    bg-gray-200 border border-gray-300 rounded px-2 py-1 text-xs opacity-50
                    ${field.width === 'half' ? 'w-[calc(50%-0.25rem)]' : ''}
                    ${field.width === 'third' ? 'w-[calc(33.333%-0.333rem)]' : ''}
                    ${field.width === 'quarter' ? 'w-[calc(25%-0.375rem)]' : ''}
                  `}
                >
                  Other field
                </div>
                {field.width === 'third' && (
                  <div className="bg-gray-200 border border-gray-300 rounded px-2 py-1 text-xs opacity-50 w-[calc(33.333%-0.333rem)]">
                    Other field
                  </div>
                )}
                {field.width === 'quarter' && (
                  <>
                    <div className="bg-gray-200 border border-gray-300 rounded px-2 py-1 text-xs opacity-50 w-[calc(25%-0.375rem)]">
                      Other
                    </div>
                    <div className="bg-gray-200 border border-gray-300 rounded px-2 py-1 text-xs opacity-50 w-[calc(25%-0.375rem)]">
                      Other
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
